// **React Imports
import React, { memo } from 'react'

// **MUI Imports
import { Box, Divider, MenuItem, Pagination, Typography } from '@mui/material'
import { DataGrid, GridCell, GridColumnMenu } from '@mui/x-data-grid'

// **Component Imports
import CustomTextField from '../textfield'

// **Utils Imports
import { pageSizeOptions } from '../../utils/constant'

// **SCSS Imports
import './index.scss'

const CustomColumnMenu = (props) => {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuColumnsItem: null // to hide hidecolumns and manage column options
      }}
    />
  )
}

// Component to show custom overlay when no data is there
const CustomNoRowsOverlay = memo(() => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%'
      }}
    >
      <Typography sx={{ fontSize: '12px' }} variant="h6" component="div">
        No data
      </Typography>
    </Box>
  )
})

// Custom cell wrapper
const CustomCellWrapper = memo((props) => {
  const newProps = { ...props }
  if (!newProps?.column?.renderCell) {
    newProps.column.renderCell = ({ row }) => {
      let cellValue = row?.[newProps?.column?.field] || ''

      if (newProps?.column?.valueGetter) {
        cellValue = newProps.column.valueGetter(cellValue)
      }

      return (
        <Typography className="restrict-cell-text-overflow" title={cellValue}>
          {cellValue}
        </Typography>
      )
    }
  }

  return <GridCell {...newProps} />
})

const CustomDataGrid = (props) => {
  const {
    autoHeight = true,
    rowHeight = 42,
    columnHeaderHeight = 42,
    disableRowSelectionOnClick = true,
    disableColumnMenu = true,
    disableColumnResize = false,
    className = '',
    needPagination = true,
    dynamicRowHeight = false,
    pageSizeValue = null,
    pageValue = null,
    pageCount = null,
    handlePaginationChange = () => {},
    paginationMode = 'client',
    ...rest
  } = props

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <DataGrid
        autoHeight={autoHeight}
        rowHeight={rowHeight}
        columnHeaderHeight={columnHeaderHeight}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        disableColumnMenu={disableColumnMenu}
        disableColumnResize={disableColumnResize}
        pageSizeOptions={pageSizeOptions}
        slots={{
          columnMenu: CustomColumnMenu,
          noRowsOverlay: CustomNoRowsOverlay,
          cell: CustomCellWrapper
        }}
        {...(needPagination
          ? {
              paginationModel: {
                page: pageValue,
                pageSize: parseInt(pageSizeValue) || 0
              },
              paginationMode
            }
          : {})}
        {...(dynamicRowHeight ? { getRowHeight: () => 'auto' } : {})}
        {...rest}
        className={`custom-data-grid-table ${className}`}
      />
      {needPagination && props?.rows?.length > 0 && (
        <>
          <Divider sx={{ m: '0 !important' }} />
          <Box className={'pagination-container'} p={2}>
            <CustomTextField
              select
              SelectProps={{
                value: pageSizeValue,
                onChange: (e) =>
                  handlePaginationChange('pageSize', e.target.value)
              }}
            >
              {pageSizeOptions.map((options, index) => (
                <MenuItem key={`${options}-${index}`} value={options}>
                  {options}
                </MenuItem>
              ))}
            </CustomTextField>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                justifyContent: 'center'
              }}
            >
              <Typography fontSize={'13px'}>
                {`${pageValue * pageSizeValue + 1}-${Math.min(
                  pageValue * pageSizeValue + pageSizeValue,
                  props.paginationMode === 'server'
                    ? props.rowCount
                    : props?.rows?.length
                )} of 
                ${props.rowCount || props?.rows?.length}`}
              </Typography>

              <Pagination
                color="primary"
                shape="rounded"
                count={pageCount}
                page={pageValue + 1}
                onChange={(_, page) => handlePaginationChange('page', page - 1)}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

export default memo(CustomDataGrid)
